import { useEffect, useRef } from "react";
import "./ClickBurst.css";

/**
 * ClickBurst
 * -------------------------------------------------------------------
 * Global subtle sci-fi click/tap response. Listens once at the document
 * level, spawns a small burst (expanding ring + central flash + 5-8
 * particles) at the pointer position, and self-cleans after ~650 ms.
 *
 * - React + CSS only. No canvas/WebGL, no libraries.
 * - `pointer-events: none` on every generated element → the underlying
 *   click still fires immediately on links / buttons / sliders.
 * - Suppresses the burst after a drag/swipe (uses a small movement
 *   threshold) so carousel swipes don't fire fireworks.
 * - `prefers-reduced-motion: reduce` → the listener no-ops entirely.
 * - Mobile / coarse-pointer → lighter version (fewer particles, no ring
 *   overshoot) so it stays elegant on small screens.
 */

const DESKTOP_PARTICLES = 7;
const MOBILE_PARTICLES  = 4;
const DRAG_SUPPRESS_PX  = 6;

export function ClickBurst() {
  const layerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;

    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const particleCount = coarse ? MOBILE_PARTICLES : DESKTOP_PARTICLES;

    let downX = 0;
    let downY = 0;
    let wasDrag = false;

    const onDown = (e) => {
      downX = e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0;
      downY = e.clientY ?? (e.touches && e.touches[0]?.clientY) ?? 0;
      wasDrag = false;
    };
    const onMove = (e) => {
      const x = e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0;
      const y = e.clientY ?? (e.touches && e.touches[0]?.clientY) ?? 0;
      if (Math.abs(x - downX) + Math.abs(y - downY) > DRAG_SUPPRESS_PX) {
        wasDrag = true;
      }
    };
    const onUp = (e) => {
      if (wasDrag) return;
      const x = e.clientX ?? (e.changedTouches && e.changedTouches[0]?.clientX);
      const y = e.clientY ?? (e.changedTouches && e.changedTouches[0]?.clientY);
      if (x == null || y == null) return;
      spawnBurst(x, y);
    };

    const spawnBurst = (x, y) => {
      const layer = layerRef.current;
      if (!layer) return;
      const burst = document.createElement("span");
      burst.className = "click-burst";
      burst.style.left = `${x}px`;
      burst.style.top  = `${y}px`;

      const flash = document.createElement("span");
      flash.className = "click-burst-flash";
      burst.appendChild(flash);

      const ring = document.createElement("span");
      ring.className = "click-burst-ring";
      burst.appendChild(ring);

      for (let i = 0; i < particleCount; i++) {
        const p = document.createElement("span");
        p.className = "click-burst-particle";
        const angle = (Math.PI * 2 * i) / particleCount + (Math.random() - 0.5) * 0.35;
        const dist = 22 + Math.random() * 14;
        p.style.setProperty("--dx", `${Math.cos(angle) * dist}px`);
        p.style.setProperty("--dy", `${Math.sin(angle) * dist}px`);
        p.style.animationDelay = `${Math.random() * 40}ms`;
        burst.appendChild(p);
      }

      layer.appendChild(burst);
      // Clean up as soon as the outer animation ends → prevents DOM growth.
      window.setTimeout(() => burst.remove(), 720);
    };

    document.addEventListener("pointerdown", onDown, { passive: true });
    document.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerup",   onUp,   { passive: true });
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup",   onUp);
    };
  }, []);

  return (
    <div
      ref={layerRef}
      className="click-burst-layer"
      aria-hidden="true"
      data-testid="click-burst-layer"
    />
  );
}
